import { useCallback, useEffect } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import {
  pendingComposerKey,
  setPendingComposerFiles,
} from "./attachments/pendingComposerFiles";
import { getAllowedRecipientTypes } from "./helpers";
import { INBOX_TABS } from "./inboxTabs";
import {
  fetchConversations,
  fetchTopics,
  searchConversations,
} from "./messagingActions";
import {
  changeInboxTab,
  clearOutgoingDraft,
  selectConversationSearchLoading,
  selectConversationSearchLoadingMore,
  selectConversationSearchNextToken,
  selectConversationSearchQuery,
  selectConversationsLoading,
  selectConversationsLoadingMore,
  selectConversationsNextToken,
  selectInboxTabId,
  selectOtherParticipantOption,
  selectOutgoingParticipant,
  selectSelectedConversationId,
  selectTopicsStatus,
  setOutgoingParticipant,
  setSelectedConversationId,
} from "./messagingSlice";
import { ParticipantOption } from "./types/common";
import { Conversation } from "./types/queries";

type Options = {
  enabled: boolean;
  clearSelectionOnUnmount?: boolean;
};

const useMessagingInboxSession = ({
  enabled,
  clearSelectionOnUnmount = false,
}: Options) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const selectedConversationId = useAppSelector(selectSelectedConversationId);
  const conversationsLoading = useAppSelector(selectConversationsLoading);
  const conversationsLoadingMore = useAppSelector(
    selectConversationsLoadingMore,
  );
  const convNextToken = useAppSelector(selectConversationsNextToken);
  const searchQuery = useAppSelector(selectConversationSearchQuery);
  const searchNextToken = useAppSelector(selectConversationSearchNextToken);
  const searchLoading = useAppSelector(selectConversationSearchLoading);
  const searchLoadingMore = useAppSelector(selectConversationSearchLoadingMore);
  const inboxTabId = useAppSelector(selectInboxTabId);
  const topicsStatus = useAppSelector(selectTopicsStatus);
  const user = useAppSelector((state) => state.user.user);
  const userId = user?.id;
  const participant = useAppSelector((state) =>
    selectOtherParticipantOption(state, selectedConversationId, userId),
  );
  const outgoingParticipant = useAppSelector(selectOutgoingParticipant);

  const tabIndex = Math.max(
    0,
    INBOX_TABS.findIndex((tab) => tab.id === inboxTabId),
  );

  const selectedParticipant = selectedConversationId
    ? participant || outgoingParticipant
    : outgoingParticipant;

  const discardUnsentCompose = useCallback(() => {
    dispatch(clearOutgoingDraft());
    setPendingComposerFiles(pendingComposerKey(null), []);
  }, [dispatch]);

  const dispatchFetchConversations = useCallback(
    (nextToken?: string) => {
      const tab = INBOX_TABS[tabIndex];
      dispatch(
        fetchConversations({
          inbox: tab.inbox,
          status: tab.status,
          ...(nextToken && { nextToken }),
          ...(!nextToken && { isInitial: true }),
        }),
      );
    },
    [dispatch, tabIndex],
  );

  const loadMore = useCallback(() => {
    if (searchQuery) {
      if (searchLoading || searchLoadingMore || !searchNextToken) return;
      dispatch(
        searchConversations({
          search: searchQuery,
          nextToken: searchNextToken,
        }),
      );
      return;
    }
    if (conversationsLoading || conversationsLoadingMore || !convNextToken)
      return;
    dispatchFetchConversations(convNextToken);
  }, [
    searchQuery,
    searchLoading,
    searchLoadingMore,
    searchNextToken,
    conversationsLoading,
    conversationsLoadingMore,
    convNextToken,
    dispatch,
    dispatchFetchConversations,
  ]);

  useEffect(() => {
    if (!clearSelectionOnUnmount) return undefined;

    return () => {
      // Read the store at unmount: a selector would still see the last render
      if (!store.getState().messaging.isFloatingWindowOpen) {
        dispatch(setSelectedConversationId(null));
        dispatch(clearOutgoingDraft());
        setPendingComposerFiles(pendingComposerKey(null), []);
      }
    };
  }, [clearSelectionOnUnmount, dispatch, store]);

  useEffect(() => {
    if (!enabled || topicsStatus !== "idle") return;
    dispatch(fetchTopics());
  }, [dispatch, enabled, topicsStatus]);

  useEffect(() => {
    if (!enabled || conversationsLoading) return;
    dispatchFetchConversations();
    // conversationsLoading is a fetch guard, not a retrigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tabIndex, enabled]);

  const handleTabChange = (
    index: number,
    changeMode: "manual" | "auto" = "manual",
  ) => {
    dispatch(changeInboxTab(INBOX_TABS[index].id));
    if (changeMode === "manual") {
      dispatch(setSelectedConversationId(null));
      discardUnsentCompose();
    }
  };

  const onConversationChange = (conversation: Conversation) => {
    discardUnsentCompose();
    dispatch(setSelectedConversationId(conversation.id));
  };

  const handleAddParticipant = (newParticipant: ParticipantOption) => {
    dispatch(setSelectedConversationId(null));
    dispatch(setOutgoingParticipant(newParticipant));
  };

  const clearSelectedConversation = useCallback(() => {
    discardUnsentCompose();
    dispatch(setSelectedConversationId(null));
  }, [discardUnsentCompose, dispatch]);

  return {
    tabIndex,
    handleTabChange,
    loadMore,
    selectedConversationId,
    selectedParticipant,
    onConversationChange,
    handleAddParticipant,
    clearSelectedConversation,
    allowedRecipientTypes: getAllowedRecipientTypes(user),
  };
};

export default useMessagingInboxSession;
